import React, { useState, useEffect } from 'react';
import createAxiosInstance from '../../utils/axiosConfig';
import { update_domain } from '../../utils/endpoints';
import Alert from './alerts/alert';

interface DomainEditSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  domainData: any | null;
  onSuccess: () => void;
}

interface DomainFormData {
  category_id: number;
  title: string;
  description: string;
  is_enabled: boolean;
  is_active: boolean;
}

const DomainEditSidebar: React.FC<DomainEditSidebarProps> = ({
  isOpen,
  onClose,
  domainData,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
  const [formData, setFormData] = useState<DomainFormData>({
    category_id: 0,
    title: '',
    description: '',
    is_enabled: true,
    is_active: true
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Populate form when domainData changes
  useEffect(() => {
    if (domainData) {
      setFormData({
        category_id: domainData.category_id || 0,
        title: domainData.title || domainData.name || '',
        description: domainData.description || domainData.shortcode || '',
        is_enabled: domainData.is_enabled ?? true,
        is_active: domainData.is_active ?? true
      });
    }
  }, [domainData]);

  // Form validation
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Shortcode is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (name === 'is_active' ? !checked : checked) : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm() || !domainData?.id) return;
    
    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.put(update_domain(domainData.id), formData);
      
      if (response.status === 200 || response.status === 201) {
        setAlert({
          message: 'Domain updated successfully!',
          type: 'success',
          show: true
        });
        
        // Call onSuccess after a short delay to show the success message
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error updating domain:', error);
      setAlert({
        message: 'Error updating domain. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      category_id: 0,
      title: '',
      description: '',
      is_enabled: true,
      is_active: true
    });
    setErrors({});
    setAlert(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="offcanvas-backdrop fade show"
        onClick={handleClose}
      ></div>
      <div
        className="offcanvas offcanvas-end show"
        tabIndex={-1}
        style={{ 
          zIndex: 1050, 
          width: '40vw',
          maxWidth: '500px'
        }}
      >
        <div className="offcanvas-header" style={{backgroundColor: 'white', borderBottom: '1px solid #dee2e6'}}>
          <h5 className="offcanvas-title" style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: '#183FCE'
          }}>
            Edit Domain
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
          ></button>
        </div>
        
        <div className="offcanvas-body">
          <div className="mb-3">
            <label htmlFor="domainTitle" className="form-label">Title:</label>
            <input
              type="text"
              className={`form-control bg-white ${errors.title ? 'is-invalid' : ''}`}
              id="domainTitle"
              name="title"
              value={formData.title}
              onChange={handleChange}
              disabled={loading}
            />
            {errors.title && (
              <div className="invalid-feedback">{errors.title}</div>
            )}
          </div>
          
          <div className="mb-3">
            <label htmlFor="domainDescription" className="form-label">Shortcode:</label>
            <input
              type="text"
              className={`form-control bg-white ${errors.description ? 'is-invalid' : ''}`}
              id="domainDescription"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={loading}
            />
            {errors.description && (
              <div className="invalid-feedback d-block text-danger" style={{fontSize: '1rem'}}>{errors.description}</div>
            )}
          </div>
          
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="domainEnabled"
                name="is_enabled"
                checked={formData.is_enabled}
                onChange={handleChange}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="domainEnabled">
                Enable Domain
              </label>
            </div>
          </div>
          
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="domainActive"
                name="is_active"
                checked={!formData.is_active}
                onChange={handleChange}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="domainActive">
                Mark as Deleted
              </label>
            </div>
          </div>
          
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-light"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-rounded"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Updating...
                </div>
              ) : (
                'Update Domain'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          show={alert.show}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default DomainEditSidebar; 